import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield, Eye, EyeOff, Heart } from 'lucide-react';

const SafetyFeaturesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7F5F8] via-[#ECE8EF] to-[#E7C6C2]">
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        {/* Back Button */}
        <Button
          onClick={() => navigate('/')}
          className="mb-8 bg-white hover:bg-gray-50 text-[#3E324D] shadow-md"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-[#6C4CA2]" />
            <h1 className="text-4xl sm:text-5xl font-bold text-[#3E324D]">
              Safety Features
            </h1>
          </div>
          <p className="text-lg text-[#6E6275] max-w-2xl mx-auto">
            Your peace and privacy come first. Here's how SheSafe Haven protects you.
          </p>
        </div>

        {/* Emergency Exit Feature */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8 border-2 border-[#E7C6C2]">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-[#E7C6C2] p-4 rounded-2xl">
              <EyeOff className="w-8 h-8 text-[#3E324D]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#3E324D] mb-2">
                Emergency Exit (Double-Tap)
              </h2>
              <p className="text-[#6E6275] text-base">
                For moments when you need to leave the app quickly or mask it for safety.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-[#F7F5F8] rounded-2xl p-6">
              <h3 className="text-lg font-bold text-[#3E324D] mb-3 flex items-center gap-2">
                <span className="bg-[#6C4CA2] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
                How to Use It
              </h3>
              <p className="text-[#6E6275] mb-4">
                We've made it simple and fast to exit when you need to:
              </p>
              
              <div className="space-y-3">
                {/* Primary method - Red exit button */}
                <div className="bg-white p-4 rounded-lg border-2 border-red-500/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-red-500 p-2 rounded-full">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    </div>
                    <p className="font-bold text-[#3E324D]">FASTEST: Red Exit Button (Recommended)</p>
                  </div>
                  <p className="text-[#6E6275] text-sm ml-14">
                    Look for the <strong className="text-red-600">red button with exit icon</strong> in the top-right corner of any page. One click instantly activates emergency exit. Always visible, always ready.
                  </p>
                </div>

                {/* Alternative methods */}
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-[#3E324D] mb-2">Alternative Methods:</p>
                  <ul className="space-y-2 text-[#6E6275] text-sm ml-4">
                    <li className="flex items-start gap-2">
                      <Heart className="w-4 h-4 mt-1" fill="#6C4CA2" />
                      <span>Double-tap the <strong>💜 SheSafe Haven logo</strong> on homepage</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#6C4CA2]">⌨️</span>
                      <span>Press <strong>Escape key</strong> on desktop/laptop</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-[#F7F5F8] rounded-2xl p-6">
              <h3 className="text-lg font-bold text-[#3E324D] mb-3 flex items-center gap-2">
                <span className="bg-[#6C4CA2] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
                What Happens
              </h3>
              <p className="text-[#6E6275] mb-3">
                The app instantly transforms into a neutral "Daily Focus" screen with:
              </p>
              <ul className="space-y-2 text-[#6E6275]">
                <li className="flex items-start gap-2">
                  <span className="text-[#6C4CA2] mt-1">•</span>
                  <span>Light beige background (no SheSafe Haven colors)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6C4CA2] mt-1">•</span>
                  <span>Simple wellness tasks (drink water, stretch, breathe)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6C4CA2] mt-1">•</span>
                  <span>Plain system fonts with neutral icons</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6C4CA2] mt-1">•</span>
                  <span>No trace of the app's true purpose</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#F7F5F8] rounded-2xl p-6">
              <h3 className="text-lg font-bold text-[#3E324D] mb-3 flex items-center gap-2">
                <span className="bg-[#6C4CA2] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">3</span>
                How to Return
              </h3>
              <p className="text-[#6E6275] mb-3">
                When it's safe, you can return to SheSafe Haven:
              </p>
              <ul className="space-y-2 text-[#6E6275]">
                <li className="flex items-start gap-2">
                  <span className="text-[#6C4CA2] mt-1">•</span>
                  <span>Tap the small <strong>Settings ⚙️</strong> icon in the top-right corner</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#6C4CA2] mt-1">•</span>
                  <span>Or simply wait 60 seconds – it will automatically revert</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#6C4CA2]/10 rounded-2xl p-6 border-2 border-[#6C4CA2]/30">
              <h3 className="text-lg font-bold text-[#3E324D] mb-3 flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#6C4CA2]" />
                Why This Matters
              </h3>
              <p className="text-[#6E6275]">
                This feature is designed to look like an ordinary productivity or wellness tool—something 
                no one would question if they glanced over your shoulder. Your safety and discretion are 
                built into every detail.
              </p>
            </div>
          </div>
        </div>

        {/* Additional Safety Tips */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-[#AAB49F]">
          <h2 className="text-2xl font-bold text-[#3E324D] mb-4 flex items-center gap-3">
            <Shield className="w-7 h-7 text-[#7C9069]" />
            Additional Safety Tips
          </h2>
          <div className="space-y-3 text-[#6E6275]">
            <div className="flex items-start gap-3">
              <span className="text-[#7C9069] font-bold">•</span>
              <p>The <strong className="text-red-600">red exit button</strong> is always in the top-right corner - one click and you're safe.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#7C9069] font-bold">•</span>
              <p>Practice clicking it once so you know exactly where it is in an emergency.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#7C9069] font-bold">•</span>
              <p>The emergency exit works on <strong>all pages</strong> - whispers, diary, journey, profile, milestones, audio, chat.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#7C9069] font-bold">•</span>
              <p>You can also press the <strong>Escape key</strong> on desktop as an alternative trigger.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#7C9069] font-bold">•</span>
              <p>Your privacy is paramount – no data is shared or stored without your consent.</p>
            </div>
          </div>
        </div>

        {/* Footer Message */}
        <div className="mt-12 text-center">
          <p className="text-[#6E6275] text-lg italic max-w-2xl mx-auto">
            "You deserve a space that not only uplifts you but protects you. 
            We've thought of every detail so you can heal in peace."
          </p>
          <p className="signature-text text-3xl text-[#6C4CA2] mt-4">
            Angie
          </p>
        </div>
      </div>
    </div>
  );
};

export default SafetyFeaturesPage;
